// Shared types for Case Inbox module

/**
 * Lookup item from API
 */
export interface LookupItem {
  id: string;
  value: string;
}

/**
 * Lookup response from API
 */
export interface LookupResponse {
  messageTypes: LookupItem[];
  priorities: LookupItem[];
}

/**
 * Message priority levels - now dynamic from API
 */
export type MessagePriority = string;

/**
 * Message types for API - now dynamic from API
 */
export type MessageType = string;

/**
 * Modal modes for UI components
 */
export type ModalMode = 'new' | 'reply' | 'forward' | 'draft';

/**
 * Associated user from API
 */
export interface AssociatedUser {
  fullName: string;
  id: string;
  role: string;
  email: string | null;
}

/**
 * Recipient for message composition
 */
export interface Recipient {
  id: string;
  name: string;
  role: string;
  role_id?: number;
  avatar?: string;
  initials?: string;
  email?: string;
  is_application_creator?: boolean;
}



/**
 * Case inbox message structure
 */
export interface InAppMessage {
  id: number;
  applicationid: number;
  subject: string;
  messageContent: string;
  message: string;
  priority: MessagePriority;
  messageType: MessageType;
  parentMessageId: number | null;
  senderId: number;
  isActive: boolean;
  isArchived: boolean;
  insertedOn: Date;
  updatedAt: Date | null;
  updatedBy: string | null;
  insertedBy: string | null;
  sender?: {
    id: number;
    firstname: string;
    lastname: string;
  };
  recipients?: {
    id: string;
    name: string;
    role: string;
    email?: string;
    isRead?: boolean;
    readAt?: Date;
  }[];
  // Read status for current user
  isRead?: boolean;
  readAt?: Date;
  recipientUserId?: number;
  attachments? : any;
}

/**
 * Query parameters for message requests
 */
export interface MessageQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  applicationId?: number;
  entityId?: number;
  caseId?: number;
  userId?: number;
  messageType?: MessageType;
  isRead?: boolean;
  sortBy?: string;
  messageView?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Request payload for creating a new message
 */
export interface CreateMessageRequest {
  applicationId: number;
  senderId: number;
  subject: string;
  message: string;
  priority?: MessagePriority;
  recipients: {
    id: string;
    name: string;
    role: string;
    email?: string;
  }[];
  parentMessageId?: number;
  messageType?: MessageType;
  attachments? : any;
}

/**
 * Request payload for updating a message
 */
export interface UpdateMessageRequest {
  messageId?:number;
  subject?: string;
  messageContent?: string;
  messageType?: MessageType;
  isRead?: boolean;
  isDeleted?: boolean;
  priority?: MessagePriority;
  attachments? : any;
  senderId?: number;
  updatedAt?: Date;
  recipients?: {
    id: string;
    name: string;
    role: string;
    email?: string;
  }[];
}

/**
 * Request payload for marking message as read/unread
 */
export interface MarkAsReadRequest {
  messageId: number;
  recipientId: number;
  isRead: boolean;
  readAt?: string | null;
}

/**
 * Paginated response structure
 */
export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * New message data for composition
 */
export interface NewMessageData {
  recipients: Recipient[];
  priority: MessagePriority;
  subject: string;
  message: string;
  parentMessageId?: number;
  attachments?: any;
  messageType?: MessageType;
}

/**
 * Priority option for UI components
 */
export interface PriorityOption {
  value: string;
  label: string;
}

/**
 * Message type option for UI components
 */
export interface MessageTypeOption {
  value: string;
  label: string;
}

/**
 * Available priority options - now populated from API
 */
export const PRIORITY_OPTIONS: PriorityOption[] = [];

 